import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import LanguageSwitcher from '../components/LanguageSwitcher';
import BusinessCard from '../components/BusinessCard';
import LineStickerCard from '../components/LineStickerCard';

const HomeContainer = styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: #1a1a3e;
    color: #f4d4a3;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 100vw solid transparent;
        border-right: 100vw solid transparent;
        border-top: 200px solid rgba(244, 212, 163, 0.05);
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 100vw solid transparent;
        border-right: 100vw solid transparent;
        border-bottom: 300px solid rgba(244, 212, 163, 0.08);
        pointer-events: none;
    }
`;

const ContentWrapper = styled.div`
    max-width: 800px;
    width: 100%;
    text-align: center;
    position: relative;
    z-index: 1;
`;

const EyeIcon = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
    position: relative;
    transition: all 0.5s ease;
    animation: ${props => props.isAnimating ? 'slideOutLeft 0.5s ease forwards' : 'slideInRight 0.5s ease forwards'};

    @keyframes slideOutLeft {
        from {
            opacity: 1;
            transform: translateX(0);
        }
        to {
            opacity: 0;
            transform: translateX(-100px);
        }
    }

    @keyframes slideInRight {
        from {
            opacity: 0;
            transform: translateX(100px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 200px;
        height: 200px;
        background: radial-gradient(circle, rgba(244, 212, 163, 0.1) 0%, transparent 70%);
        border-radius: 50%;
        pointer-events: none;
    }

    img {
        width: 100px;
        height: 100px;
        opacity: 0.9;
        transition: all 0.3s ease;
        filter: drop-shadow(0 4px 20px rgba(244, 212, 163, 0.3));
        position: relative;
        z-index: 1;

        &:hover {
            opacity: 1;
            transform: scale(1.1);
            filter: drop-shadow(0 6px 30px rgba(244, 212, 163, 0.5));
        }

        @media (max-width: 768px) {
            width: 80px;
            height: 80px;
        }
    }
`;

const TitleWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 15px;
    }
`;

const Title = styled.h1`
    font-size: 3.5rem;
    margin: 0;
    font-weight: 700;
    color: #f4d4a3;
    letter-spacing: 2px;
    text-shadow: 0 2px 10px rgba(244, 212, 163, 0.3);
    transition: all 0.5s ease;
    animation: ${props => props.isAnimating ? 'slideOutLeft 0.5s ease forwards' : 'slideInRight 0.5s ease forwards'};

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }
`;

const Subtitle = styled.h2`
    font-size: 1.8rem;
    margin-bottom: 30px;
    font-weight: 300;
    color: #f4d4a3;
    opacity: 0.9;
    letter-spacing: 1px;
    transition: all 0.5s ease;
    animation: ${props => props.isAnimating ? 'slideOutLeft 0.5s ease forwards' : 'slideInRight 0.5s ease 0.1s forwards'};
`;

const Description = styled.p`
    font-size: 1.2rem;
    line-height: 1.8;
    margin-bottom: 16px;
    color: #f4d4a3;
    opacity: 0.85;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    transition: all 0.5s ease;
`;

const slideInRight = keyframes`
    from { opacity: 0; transform: translateX(100px); }
    to   { opacity: 1; transform: translateX(0); }
`;

const slideOutLeft = keyframes`
    from { opacity: 1; transform: translateX(0); }
    to   { opacity: 0; transform: translateX(-100px); }
`;

const QuoteWrapper = styled.div`
    margin-bottom: 40px;
    min-height: 3.2em;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const Quote = styled.p`
    font-size: 1.05rem;
    line-height: 1.6;
    color: #f4d4a3;
    opacity: 0.7;
    font-style: italic;
    letter-spacing: 0.5px;
    max-width: 520px;
    margin: 0 auto;
    animation: ${props => props.$fading ? slideOutLeft : slideInRight} 0.5s ease forwards;

    &::before { content: '"'; }
    &::after  { content: '"'; }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 20px;
    justify-content: center;
    flex-wrap: wrap;
    transition: all 0.5s ease;
`;

const StyledLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 15px 40px;
    background: rgba(244, 212, 163, 0.1);
    color: #f4d4a3;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1.1rem;
    border: 1px solid rgba(244, 212, 163, 0.3);
    transition: all 0.3s ease;
`;

const StyledButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 15px 40px;
    background: rgba(244, 212, 163, 0.1);
    color: #f4d4a3;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1.1rem;
    border: 1px solid rgba(244, 212, 163, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;
    font-family: inherit;
`;

const Footer = styled.footer`
    margin-top: 60px;
    padding-top: 30px;
    border-top: 1px solid rgba(244, 212, 163, 0.2);
    text-align: center;
    display: flex;
    gap: 20px;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
`;

const FooterLink = styled(Link)`
    color: rgba(244, 212, 163, 0.7);
    text-decoration: none;
    font-size: 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 6px;
`;

const FooterButton = styled.button`
    color: rgba(244, 212, 163, 0.7);
    font-size: 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
`;

const QUOTE_INTERVAL = 5000;

const HomePage = () => {
    const { t, i18n } = useTranslation();
    const [showBusinessCard, setShowBusinessCard] = useState(false);
    const [showLineStickerCard, setShowLineStickerCard] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [prevLanguage, setPrevLanguage] = useState(i18n.language);
    const [quoteIdx, setQuoteIdx] = useState(0);
    const [quoteFading, setQuoteFading] = useState(false);

    const taglines = t('home.taglines', { returnObjects: true });

    const cycleQuote = useCallback(() => {
        setQuoteFading(true);
        setTimeout(() => {
            setQuoteIdx(prev => (prev + 1) % taglines.length);
            setQuoteFading(false);
        }, 500);
    }, [taglines.length]);

    useEffect(() => {
        const id = setInterval(cycleQuote, QUOTE_INTERVAL);
        return () => clearInterval(id);
    }, [cycleQuote]);

    useEffect(() => {
        const handleLanguageChange = (lng) => {
            if (lng !== prevLanguage) {
                setIsAnimating(true);
                setPrevLanguage(lng);
                setQuoteIdx(0);
                setQuoteFading(false);
                setTimeout(() => {
                    setIsAnimating(false);
                }, 500);
            }
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n, prevLanguage]);

    return (
        <HomeContainer>
            <ContentWrapper>
                <EyeIcon isAnimating={isAnimating}>
                    <img src="/images/creature.png" alt="Creature" />
                </EyeIcon>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <LanguageSwitcher inline={true} />
                </div>
                <TitleWrapper>
                    <Title isAnimating={isAnimating}>{t('home.title')}</Title>
                </TitleWrapper>
                <Subtitle isAnimating={isAnimating}>{t('home.subtitle')}</Subtitle>
                <Description isAnimating={isAnimating}>
                    {t('home.description')}
                    <br />
                    {t('home.currentJob')}
                </Description>
                <QuoteWrapper>
                    <Quote $fading={quoteFading} key={quoteIdx}>
                        {Array.isArray(taglines) ? taglines[quoteIdx] : taglines}
                    </Quote>
                </QuoteWrapper>

                <ButtonGroup isAnimating={isAnimating}>
                    <StyledLink to="/music" state={{ fromHome: true }}>🎵 {t('home.music')}</StyledLink>
                    <StyledLink to="/projects">💡 {t('home.projects')}</StyledLink>
                    <StyledLink to="/blog">📝 {t('home.blog')}</StyledLink>
                    <StyledLink to="/ai-tools">🤖 {t('home.aiTools')}</StyledLink>
                    <StyledLink to="/language-learning">📚 {t('home.languageLearning')}</StyledLink>
                    <StyledButton onClick={() => setShowLineStickerCard(true)}>
                        🐴 {t('home.lineSticker')}
                    </StyledButton>
                </ButtonGroup>

                <Footer isAnimating={isAnimating}>
                    <FooterLink to="/resume">📄 {t('home.resume')}</FooterLink>
                    <FooterButton onClick={() => setShowBusinessCard(true)}>
                        ✉️ {t('home.contact')}
                    </FooterButton>
                    <FooterLink to="/changelog">📝 Changelog</FooterLink>
                </Footer>
            </ContentWrapper>

            {showBusinessCard && (
                <BusinessCard onClose={() => setShowBusinessCard(false)} />
            )}

            {showLineStickerCard && (
                <LineStickerCard onClose={() => setShowLineStickerCard(false)} />
            )}
        </HomeContainer>
    );
};

export default HomePage;

